/**
 * Auth Store
 *
 * Zustand store for authentication state management
 */

import { create } from 'zustand';
import type { User as SupabaseUser, Session } from '@supabase/supabase-js';
import type { User } from '@/types/user';

interface AuthState {
  /** Supabase Auth user */
  authUser: SupabaseUser | null;
  /** App user from public.users table */
  user: User | null;
  /** Current session */
  session: Session | null;
  /** Loading state for initial auth check */
  isLoading: boolean;
  /** Whether auth has been initialized */
  isInitialized: boolean;

  // Actions
  setAuthUser: (user: SupabaseUser | null) => void;
  setUser: (user: User | null) => void;
  setSession: (session: Session | null) => void;
  setLoading: (loading: boolean) => void;
  setInitialized: (initialized: boolean) => void;
  reset: () => void;
}

const initialState = {
  authUser: null,
  user: null,
  session: null,
  isLoading: true,
  isInitialized: false,
};

export const useAuthStore = create<AuthState>((set) => ({
  ...initialState,

  setAuthUser: (authUser) => set({ authUser }),
  setUser: (user) => set({ user }),
  setSession: (session) => set({ session }),
  setLoading: (isLoading) => set({ isLoading }),
  setInitialized: (isInitialized) => set({ isInitialized }),
  // Reset user data but keep initialized state (for logout)
  reset: () => set({
    authUser: null,
    user: null,
    session: null,
    isLoading: false,
    isInitialized: true,
  }),
}));

/**
 * Selector: Check if user is authenticated
 */
export const selectIsAuthenticated = (state: AuthState) =>
  state.session !== null && state.authUser !== null;

/**
 * Selector: Check if user has completed onboarding (language set)
 */
export const selectHasCompletedOnboarding = (state: AuthState) =>
  state.user !== null && state.user.language !== undefined;

/**
 * Selector: Check if user has completed tag setup (username set)
 */
export const selectHasCompletedTagSetup = (state: AuthState) =>
  state.user !== null && 
  state.user.username !== undefined && 
  state.user.discriminator !== undefined;

/**
 * Selector: Check if user needs to complete username setup
 */
export const selectNeedsUsernameSetup = (state: AuthState) =>
  state.user !== null && 
  state.user.language !== undefined && 
  state.user.username === undefined;

/**
 * Selector: Get user's tag
 */
export const selectUserTag = (state: AuthState) =>
  state.user?.tag;

/**
 * Selector: Get auth loading state
 */
export const selectIsAuthLoading = (state: AuthState) =>
  state.isLoading || !state.isInitialized;
